import React from 'react';

import { ColumnMenuItem, DataGrid } from '@components/admin/DataGrid';
import { ErrorResult } from '@components/admin/ErrorResult';
import { TextInput } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { Pagination } from '@components/Pagination';
import {
  TRACKING_DATA_PAGE_SIZE,
  useTrackingDataList,
} from '@hooks/queries/dashboard/useTrackingDataList';
import { useSearch } from '@hooks/useSearch';
import { allTransportStatus, TrackingData, transportStatus } from '@models/TrackingData';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings, stringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { toJST } from '@utils/types';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { Link } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '追跡管理',
    index: 'NO.',
    d2dNo: 'D2D番号',
    transportStatus: '輸送状況',
    dateTime: '日付',
    exportMovedInInputStatus: '入庫情報入力状況',
    inputDone: '入力済',
    inputAwait: '未入力',
    details: '詳細',
    seeAll: '全部見る' as const,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
    '& a:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
}));

const searchType = t.type({
  d2dNo: h.omittable(h.string()),
  page: h.omittable(h.number().cast()),
  transportStatus: h.omittable(transportStatus),
  exportMovedInInputStatus: h.omittable(h.boolean().booleanish()),
});

const schema = t.type({
  d2dNo: stringField(strings.d2dNo),
});

type TrackingDataItem = TrackingData & {
  details: string;
  id: any;
  index: number;
};

export const CargoTracking: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(searchType);
  const trackingDataList = useTrackingDataList({
    page: search.page ?? 1,
    track_no: search.d2dNo,
    transport_status: search.transportStatus,
    export_moved_in_status: search.exportMovedInInputStatus,
  });

  const initialValues = {
    d2dNo: search.d2dNo ?? '',
  };

  const transportStatusMenuItems: ColumnMenuItem[] = [
    ...allTransportStatus.map((s) => ({
      label: s,
      selected: s === search.transportStatus,
      onClick: () =>
        updateSearch({
          transportStatus: s,
          page: 1,
        }),
    })),
    {
      label: strings.seeAll,
      selected: !!search.transportStatus === false,
      onClick: () =>
        updateSearch({
          transportStatus: undefined,
          page: 1,
        }),
    },
  ];

  const exportMovedInInputStatusMenuItems: ColumnMenuItem[] = [
    {
      label: strings.inputDone,
      selected: search.exportMovedInInputStatus === true,
      onClick: () =>
        updateSearch({
          exportMovedInInputStatus: true,
          page: 1,
        }),
    },
    {
      label: strings.inputAwait,
      selected: search.exportMovedInInputStatus === false,
      onClick: () =>
        updateSearch({
          exportMovedInInputStatus: false,
          page: 1,
        }),
    },
    {
      label: strings.seeAll,
      selected: search.exportMovedInInputStatus === undefined,
      onClick: () =>
        updateSearch({
          exportMovedInInputStatus: undefined,
          page: 1,
        }),
    },
  ];

  const renderPagination = () => {
    const pageCount = Math.ceil(trackingDataList.data!.count / TRACKING_DATA_PAGE_SIZE);

    return (
      <Pagination
        page={Math.min(Math.max(search.page ?? 1, 0), pageCount) - 1}
        count={pageCount}
        onChange={(page) => updateSearch({ page: page + 1 })}
      />
    );
  };

  return (
    <Layout
      title={strings.title}
      loading={trackingDataList.isLoading}
      errors={[trackingDataList.error]}
    >
      {() => (
        <div className={classes.container}>
          <SectionTitle>{strings.title}</SectionTitle>

          <div style={{ height: 20 }} />

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={createValidate(schema)}
            onSubmit={(values) => {
              updateSearch({
                ...values,
                page: 1,
              });
            }}
          >
            <Form className={classes.flexInline}>
              <TextInput name="d2dNo" label={strings.d2dNo} />

              <div style={{ width: 32 }} />

              <Button
                text={formStrings.search}
                color="primary"
                type="submit"
                shape="round"
                size="small"
              />
            </Form>
          </Formik>

          <div style={{ height: 40 }} />

          <DataGrid<TrackingDataItem>
            columns={[
              {
                field: 'index',
                label: strings.index,
              },
              {
                field: 'track_no',
                label: strings.d2dNo,
              },
              {
                field: 'transport_status',
                label: strings.transportStatus,
                menu: {
                  items: transportStatusMenuItems,
                },
              },
              {
                field: 'created_at',
                label: strings.dateTime,
              },
              {
                field: 'export_moved_in_status',
                label: strings.exportMovedInInputStatus,
                menu: {
                  items: exportMovedInInputStatusMenuItems,
                },
              },
              {
                field: 'details',
                label: '',
              },
            ]}
            renderRow={{
              index: String,
              track_no: String,
              transport_status: String,
              created_at: (iso) => toJST(new Date(iso)),
              export_moved_in_status: (v) => (v ? strings.inputDone : strings.inputAwait),
              details: (_, row) => (
                <Link to={`${url('/admin/widgets/CargoTrackingDetails')}?d2dNo=${row.track_no}`}>
                  {strings.details}
                </Link>
              ),
            }}
            rows={trackingDataList.data!.results.map((row, i) => ({
              ...row,
              index: i + 1 + 10 * ((search.page ?? 1) - 1),
              details: '',
            }))}
            emptyFallback={
              <ErrorResult
                onAction={() =>
                  updateSearch({
                    d2dNo: undefined,
                    page: undefined,
                    transportStatus: undefined,
                    exportMovedInInputStatus: undefined,
                  })
                }
              />
            }
          />

          <div style={{ height: 36 }} />

          <div className={classes.paginationContainer}>{renderPagination()}</div>
        </div>
      )}
    </Layout>
  );
};
